import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import './AdminMobile.css';

interface NotificationSetting {
  id: number;
  notification_type: string;
  enabled: boolean;
  target_audience: string;
  target_user_ids: number[] | null;
  schedule_day: number | null;
  schedule_hour: number | null;
  schedule_minute: number | null;
  min_distance_km: number | null;
  quiet_hours_enabled: boolean;
  quiet_hours_start: number;
  quiet_hours_end: number;
  max_per_user_per_day: number;
  title_template: string | null;
  body_template: string | null;
  updated_at: string | null;
}

interface NotificationLog {
  id: number;
  notification_type: string;
  user_id: number;
  user_name: string | null;
  title: string;
  body: string;
  status: string;
  error_message: string | null;
  related_entity_type: string | null;
  related_entity_id: number | null;
  created_at: string;
}

interface DeviceToken {
  id: number;
  user_id: number;
  user_name: string | null;
  user_club: boolean;
  platform: string;
  device_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

interface Stats {
  total_notifications: number;
  last_24h: number;
  last_7d: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  device_tokens: {
    active: number;
    total: number;
    ios: number;
    android: number;
  };
  apns: {
    configured: boolean;
    environment: string;
  };
  fcm: {
    configured: boolean;
  };
}

interface AppVersionData {
  version_code: number;
  version_name: string;
  updated_at: string | null;
  updated_by: number | null;
  from_db: boolean;
}

const AdminMobile: React.FC = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<'settings' | 'logs' | 'devices' | 'android_ver'>('settings');
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [devices, setDevices] = useState<DeviceToken[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [editingSetting, setEditingSetting] = useState<NotificationSetting | null>(null);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testTitle, setTestTitle] = useState('Test Notification');
  const [testBody, setTestBody] = useState('This is a test notification from Alps Epic.');
  const [testSending, setTestSending] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Android Version state
  const [appVersion, setAppVersion] = useState<AppVersionData | null>(null);
  const [editVersionName, setEditVersionName] = useState('');
  const [editVersionCode, setEditVersionCode] = useState('');
  const [versionSaving, setVersionSaving] = useState(false);
  const [versionLoading, setVersionLoading] = useState(false);

  // Pagination for logs
  const [logsPage, setLogsPage] = useState(1);
  const [logsPagination, setLogsPagination] = useState({ pages: 1, total: 0 });
  const [logsFilter, setLogsFilter] = useState<{ type: string; status: string }>({ type: '', status: '' });

  const getNotificationTypeLabel = (type: string): string => {
    const key = `adminMobile.notifTypes.${type}`;
    const translated = t(key);
    return translated !== key ? translated : type;
  };

  const getAudienceLabel = (audience: string): string => {
    const key = `adminMobile.audiences.${audience}`;
    const translated = t(key);
    return translated !== key ? translated : audience;
  };

  const getDayLabel = (dayIndex: number): string => {
    const key = `adminMobile.days.${dayIndex}`;
    const translated = t(key);
    return translated !== key ? translated : String(dayIndex);
  };

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/notifications/settings`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/notifications/stats`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: logsPage.toString(),
        per_page: '50'
      });
      if (logsFilter.type) params.append('type', logsFilter.type);
      if (logsFilter.status) params.append('status', logsFilter.status);

      const response = await fetch(`${API_BASE_URL}/api/admin/notifications/logs?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        setLogs(data.logs);
        setLogsPagination({ pages: data.pagination.pages, total: data.pagination.total });
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  }, [logsPage, logsFilter]);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/notifications/device-tokens`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        setDevices(data.tokens);
      }
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    }
  }, []);

  const fetchAppVersion = useCallback(async () => {
    setVersionLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/app-version`, { credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        setAppVersion(data);
        setEditVersionName(data.version_name);
        setEditVersionCode(String(data.version_code));
      }
    } catch (error) {
      console.error('Failed to fetch app version:', error);
    }
    setVersionLoading(false);
  }, []);

  const handleSaveVersion = async () => {
    const code = parseInt(editVersionCode);
    if (!editVersionName.trim() || isNaN(code)) {
      showToast('Version name and valid version code are required', 'error');
      return;
    }
    setVersionSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/app-version`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ version_name: editVersionName.trim(), version_code: code })
      });
      const data = await response.json();
      if (response.ok) {
        setAppVersion({ ...data, from_db: true, updated_by: null });
        showToast('Android version saved', 'success');
      } else {
        showToast(data.error || 'Failed to save', 'error');
      }
    } catch (error) {
      showToast('Failed to save version', 'error');
    }
    setVersionSaving(false);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSettings, fetchStats]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'devices') {
      fetchDevices();
    } else if (activeTab === 'android_ver') {
      fetchAppVersion();
    }
  }, [activeTab, fetchLogs, fetchDevices, fetchAppVersion]);

  const handleToggleEnabled = async (setting: NotificationSetting) => {
    setSaving(setting.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/notifications/settings/${setting.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !setting.enabled })
      });

      if (response.ok) {
        setSettings(prev => prev.map(s =>
          s.id === setting.id ? { ...s, enabled: !s.enabled } : s
        ));
        const typeLabel = getNotificationTypeLabel(setting.notification_type);
        const statusLabel = !setting.enabled ? t('adminMobile.enabled') : t('adminMobile.disabled');
        showToast(`${typeLabel} ${statusLabel}`, 'success');
      }
    } catch (error) {
      showToast(t('adminMobile.saveError'), 'error');
    }
    setSaving(null);
  };

  const handleSaveSetting = async () => {
    if (!editingSetting) return;

    setSaving(editingSetting.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/notifications/settings/${editingSetting.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editingSetting)
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => prev.map(s =>
          s.id === editingSetting.id ? data.setting : s
        ));
        setEditingSetting(null);
        showToast(t('adminMobile.settingsSaved'), 'success');
      }
    } catch (error) {
      showToast(t('adminMobile.saveError'), 'error');
    }
    setSaving(null);
  };

  const handleSendTest = async () => {
    setTestSending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/notifications/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: testTitle, body: testBody })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        showToast(t('adminMobile.testSent'), 'success');
        setTestModalOpen(false);
        fetchStats();
        if (activeTab === 'logs') fetchLogs();
      } else {
        showToast(data.error || t('adminMobile.testFailed'), 'error');
      }
    } catch (error) {
      showToast(t('adminMobile.testFailed'), 'error');
    }
    setTestSending(false);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const locale = language === 'pl' ? 'pl-PL' : language === 'de' ? 'de-DE' : language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US';
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasAnyPushConfigured = stats?.apns.configured || stats?.fcm?.configured;

  if (loading) {
    return <div className="admin-mobile-loading">{t('common.loading')}</div>;
  }

  return (
    <div className="admin-mobile">
      {/* Stats Header */}
      <div className="admin-mobile-stats">
        <div className="stat-card">
          <span className="stat-value">{stats?.device_tokens.active || 0}</span>
          <span className="stat-label">{t('adminMobile.activeDevices')}</span>
        </div>
        <div className="stat-card">
          <div className="stat-value platform-counts">
            <span className="platform-count-item">
              <span className="platform-icon ios-icon">iOS</span>
              <span>{stats?.device_tokens.ios || 0}</span>
            </span>
            <span className="platform-count-divider">/</span>
            <span className="platform-count-item">
              <span className="platform-icon android-icon">And</span>
              <span>{stats?.device_tokens.android || 0}</span>
            </span>
          </div>
          <span className="stat-label">{t('adminMobile.iosDevices')} / {t('adminMobile.androidDevices')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats?.last_24h || 0}</span>
          <span className="stat-label">{t('adminMobile.last24h')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats?.last_7d || 0}</span>
          <span className="stat-label">{t('adminMobile.last7d')}</span>
        </div>
        <div className={`stat-card ${stats?.apns.configured ? 'success' : 'danger'}`}>
          <span className="stat-value">{stats?.apns.configured ? 'OK' : '-'}</span>
          <span className="stat-label">APNs ({stats?.apns.environment})</span>
        </div>
        <div className={`stat-card ${stats?.fcm?.configured ? 'success' : 'danger'}`}>
          <span className="stat-value">{stats?.fcm?.configured ? 'OK' : '-'}</span>
          <span className="stat-label">{t('adminMobile.fcmConfigured')}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-mobile-tabs">
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          {t('adminMobile.tabs.settings')}
        </button>
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          {t('adminMobile.tabs.logs')} ({stats?.total_notifications || 0})
        </button>
        <button
          className={`tab ${activeTab === 'devices' ? 'active' : ''}`}
          onClick={() => setActiveTab('devices')}
        >
          {t('adminMobile.tabs.devices')} ({stats?.device_tokens.total || 0})
        </button>
        <button
          className={`tab ${activeTab === 'android_ver' ? 'active' : ''}`}
          onClick={() => setActiveTab('android_ver')}
        >
          Android Ver
        </button>
        <button
          className="tab test-btn"
          onClick={() => setTestModalOpen(true)}
          disabled={!hasAnyPushConfigured}
        >
          {t('adminMobile.sendTest')}
        </button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="settings-list">
          {settings.map(setting => (
            <div key={setting.id} className={`setting-card ${!setting.enabled ? 'disabled' : ''}`}>
              <div className="setting-header">
                <div className="setting-title">
                  <h3>{getNotificationTypeLabel(setting.notification_type)}</h3>
                  <span className="setting-type-badge">{setting.notification_type}</span>
                </div>
                <div className="setting-actions">
                  <button
                    className={`toggle-btn ${setting.enabled ? 'on' : 'off'}`}
                    onClick={() => handleToggleEnabled(setting)}
                    disabled={saving === setting.id}
                  >
                    {setting.enabled ? 'ON' : 'OFF'}
                  </button>
                  <button
                    className="edit-btn"
                    onClick={() => setEditingSetting({ ...setting })}
                  >
                    {t('common.edit')}
                  </button>
                </div>
              </div>

              <div className="setting-details">
                <div className="detail-row">
                  <span className="detail-label">{t('adminMobile.targetAudience')}:</span>
                  <span className="detail-value">{getAudienceLabel(setting.target_audience)}</span>
                </div>

                {setting.notification_type === 'new_activity' && setting.min_distance_km && (
                  <div className="detail-row">
                    <span className="detail-label">{t('adminMobile.minDistance')}:</span>
                    <span className="detail-value">{setting.min_distance_km} km</span>
                  </div>
                )}

                {setting.notification_type === 'weekly_summary' && setting.schedule_day !== null && (
                  <div className="detail-row">
                    <span className="detail-label">{t('adminMobile.schedule')}:</span>
                    <span className="detail-value">
                      {getDayLabel(setting.schedule_day)}, {String(setting.schedule_hour).padStart(2, '0')}:{String(setting.schedule_minute || 0).padStart(2, '0')}
                    </span>
                  </div>
                )}

                {setting.quiet_hours_enabled && (
                  <div className="detail-row">
                    <span className="detail-label">{t('adminMobile.quietHours')}:</span>
                    <span className="detail-value">
                      {String(setting.quiet_hours_start).padStart(2, '0')}:00 - {String(setting.quiet_hours_end).padStart(2, '0')}:00
                    </span>
                  </div>
                )}

                <div className="detail-row">
                  <span className="detail-label">{t('adminMobile.rateLimit')}:</span>
                  <span className="detail-value">{setting.max_per_user_per_day} / {t('adminMobile.perUserPerDay')}</span>
                </div>

                {setting.title_template && (
                  <div className="detail-row template">
                    <span className="detail-label">{t('adminMobile.titleTemplate')}:</span>
                    <code className="detail-value">{setting.title_template}</code>
                  </div>
                )}

                {setting.body_template && (
                  <div className="detail-row template">
                    <span className="detail-label">{t('adminMobile.bodyTemplate')}:</span>
                    <code className="detail-value">{setting.body_template}</code>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="logs-section">
          <div className="logs-filters">
            <select
              value={logsFilter.type}
              onChange={e => { setLogsFilter(f => ({ ...f, type: e.target.value })); setLogsPage(1); }}
            >
              <option value="">{t('adminMobile.allTypes')}</option>
              <option value="new_activity">{t('adminMobile.notifTypes.new_activity')}</option>
              <option value="weekly_summary">{t('adminMobile.notifTypes.weekly_summary')}</option>
              <option value="new_article">{t('adminMobile.notifTypes.new_article')}</option>
              <option value="workout_reminder">{t('adminMobile.notifTypes.workout_reminder')}</option>
              <option value="test">{t('adminMobile.notifTypes.test')}</option>
            </select>
            <select
              value={logsFilter.status}
              onChange={e => { setLogsFilter(f => ({ ...f, status: e.target.value })); setLogsPage(1); }}
            >
              <option value="">{t('adminMobile.allStatuses')}</option>
              <option value="sent">{t('adminMobile.sent')}</option>
              <option value="delivered">{t('adminMobile.delivered')}</option>
              <option value="failed">{t('adminMobile.failed')}</option>
            </select>
            <button className="refresh-btn" onClick={fetchLogs}>{t('common.refresh')}</button>
          </div>

          <table className="logs-table">
            <thead>
              <tr>
                <th>{t('adminMobile.date')}</th>
                <th>{t('adminMobile.notificationType')}</th>
                <th>{t('adminMobile.user')}</th>
                <th>{t('adminMobile.titleTemplate')}</th>
                <th>{t('adminMobile.status')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={5} className="empty">{t('adminMobile.noLogs')}</td></tr>
              ) : logs.map(log => (
                <tr key={log.id}>
                  <td className="date">{formatDate(log.created_at)}</td>
                  <td>
                    <span className={`type-badge ${log.notification_type}`}>
                      {getNotificationTypeLabel(log.notification_type)}
                    </span>
                  </td>
                  <td>{log.user_name || `#${log.user_id}`}</td>
                  <td className="title-cell">
                    <div className="log-title">{log.title}</div>
                    <div className="log-body">{log.body}</div>
                  </td>
                  <td>
                    <span className={`status-badge ${log.status}`}>{log.status}</span>
                    {log.error_message && (
                      <div className="error-msg" title={log.error_message}>
                        {log.error_message.substring(0, 30)}...
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {logsPagination.pages > 1 && (
            <div className="pagination">
              <button
                disabled={logsPage === 1}
                onClick={() => setLogsPage(p => p - 1)}
              >
                {t('common.previous')}
              </button>
              <span>{t('adminMobile.pageOf', { page: logsPage, pages: logsPagination.pages, total: logsPagination.total })}</span>
              <button
                disabled={logsPage === logsPagination.pages}
                onClick={() => setLogsPage(p => p + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Devices Tab */}
      {activeTab === 'devices' && (
        <div className="devices-section">
          <table className="devices-table">
            <thead>
              <tr>
                <th>{t('adminMobile.user')}</th>
                <th>{t('adminMobile.platform')}</th>
                <th>{t('adminMobile.device')}</th>
                <th>{t('adminMobile.club')}</th>
                <th>{t('adminMobile.status')}</th>
                <th>{t('adminMobile.lastUpdated')}</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 ? (
                <tr><td colSpan={6} className="empty">{t('adminMobile.noDeviceTokens')}</td></tr>
              ) : devices.map(device => (
                <tr key={device.id}>
                  <td>{device.user_name || `#${device.user_id}`}</td>
                  <td>
                    <span className={`platform-badge ${device.platform}`}>
                      {device.platform === 'ios' ? 'iOS' : device.platform === 'android' ? 'Android' : device.platform.toUpperCase()}
                    </span>
                  </td>
                  <td>{device.device_name || '-'}</td>
                  <td>
                    {device.user_club ? (
                      <span className="club-badge yes">{t('adminMobile.yes')}</span>
                    ) : (
                      <span className="club-badge no">{t('adminMobile.no')}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${device.is_active ? 'active' : 'inactive'}`}>
                      {device.is_active ? t('adminMobile.active') : t('adminMobile.inactive')}
                    </span>
                  </td>
                  <td>{device.updated_at ? formatDate(device.updated_at) : formatDate(device.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Android Version Tab */}
      {activeTab === 'android_ver' && (
        <div className="settings-list">
          <div className="setting-card">
            <div className="setting-header">
              <div className="setting-title">
                <h3>Android App Version</h3>
              </div>
            </div>
            {versionLoading ? (
              <p style={{ color: '#9ca3af', padding: '12px 0' }}>Loading...</p>
            ) : (
              <div className="setting-details">
                <div className="detail-row">
                  <span className="detail-label">Current version:</span>
                  <span className="detail-value">
                    <strong>{appVersion?.version_name || '-'}</strong> (code: {appVersion?.version_code ?? '-'})
                  </span>
                </div>
                {appVersion?.updated_at && (
                  <div className="detail-row">
                    <span className="detail-label">Last updated:</span>
                    <span className="detail-value">{formatDate(appVersion.updated_at)}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Source:</span>
                  <span className="detail-value">{appVersion?.from_db ? 'Database' : 'Default (hardcoded)'}</span>
                </div>

                <div style={{ marginTop: '20px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>Edit Version</h4>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '120px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Version Name</label>
                      <input
                        type="text"
                        value={editVersionName}
                        onChange={e => setEditVersionName(e.target.value)}
                        placeholder="e.g. 1.2.0"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '120px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Version Code</label>
                      <input
                        type="number"
                        value={editVersionCode}
                        onChange={e => setEditVersionCode(e.target.value)}
                        placeholder="e.g. 3"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                      />
                    </div>
                    <button
                      onClick={handleSaveVersion}
                      disabled={versionSaving}
                      style={{
                        padding: '8px 24px',
                        background: '#243b53',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: versionSaving ? 'not-allowed' : 'pointer',
                        opacity: versionSaving ? 0.6 : 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {versionSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingSetting && (
        <div className="modal-overlay" onClick={() => setEditingSetting(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{t('common.edit')}: {getNotificationTypeLabel(editingSetting.notification_type)}</h2>

            <div className="form-group">
              <label>{t('adminMobile.targetAudience')}</label>
              <select
                value={editingSetting.target_audience}
                onChange={e => setEditingSetting({ ...editingSetting, target_audience: e.target.value })}
              >
                <option value="all">{t('adminMobile.audiences.all')}</option>
                <option value="club">{t('adminMobile.audiences.club')}</option>
              </select>
            </div>

            {editingSetting.notification_type === 'new_activity' && (
              <div className="form-group">
                <label>{t('adminMobile.minDistance')} (km)</label>
                <input
                  type="number"
                  step="0.5"
                  value={editingSetting.min_distance_km || 0}
                  onChange={e => setEditingSetting({ ...editingSetting, min_distance_km: parseFloat(e.target.value) })}
                />
                <span className="hint">{t('adminMobile.minDistanceHint')}</span>
              </div>
            )}

            {editingSetting.notification_type === 'weekly_summary' && (
              <>
                <div className="form-group">
                  <label>{t('adminMobile.dayOfWeek')}</label>
                  <select
                    value={editingSetting.schedule_day ?? 0}
                    onChange={e => setEditingSetting({ ...editingSetting, schedule_day: parseInt(e.target.value) })}
                  >
                    {[0, 1, 2, 3, 4, 5, 6].map(i => (
                      <option key={i} value={i}>{getDayLabel(i)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('adminMobile.hour')}</label>
                    <input
                      type="number"
                      min={0}
                      max={23}
                      value={editingSetting.schedule_hour ?? 18}
                      onChange={e => setEditingSetting({ ...editingSetting, schedule_hour: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('adminMobile.minute')}</label>
                    <input
                      type="number"
                      min={0}
                      max={59}
                      value={editingSetting.schedule_minute ?? 0}
                      onChange={e => setEditingSetting({ ...editingSetting, schedule_minute: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={editingSetting.quiet_hours_enabled}
                  onChange={e => setEditingSetting({ ...editingSetting, quiet_hours_enabled: e.target.checked })}
                />
                {t('adminMobile.quietHoursDesc')}
              </label>
            </div>

            {editingSetting.quiet_hours_enabled && (
              <div className="form-row">
                <div className="form-group">
                  <label>{t('adminMobile.fromHour')}</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={editingSetting.quiet_hours_start}
                    onChange={e => setEditingSetting({ ...editingSetting, quiet_hours_start: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('adminMobile.toHour')}</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={editingSetting.quiet_hours_end}
                    onChange={e => setEditingSetting({ ...editingSetting, quiet_hours_end: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{t('adminMobile.dailyLimitPerUser')}</label>
              <input
                type="number"
                min={1}
                max={100}
                value={editingSetting.max_per_user_per_day}
                onChange={e => setEditingSetting({ ...editingSetting, max_per_user_per_day: parseInt(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>{t('adminMobile.titleTemplate')}</label>
              <input
                type="text"
                value={editingSetting.title_template || ''}
                onChange={e => setEditingSetting({ ...editingSetting, title_template: e.target.value })}
                placeholder="{user_name}, {activity_title}, {distance}..."
              />
              <span className="hint">{t('adminMobile.templateHint')}: {'{user_name}'}, {'{activity_title}'}, {'{distance}'}, {'{workout_type}'}</span>
            </div>

            <div className="form-group">
              <label>{t('adminMobile.bodyTemplate')}</label>
              <textarea
                value={editingSetting.body_template || ''}
                onChange={e => setEditingSetting({ ...editingSetting, body_template: e.target.value })}
                placeholder={t('adminMobile.bodyPlaceholder')}
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setEditingSetting(null)}>{t('common.cancel')}</button>
              <button
                className="save-btn"
                onClick={handleSaveSetting}
                disabled={saving === editingSetting.id}
              >
                {saving === editingSetting.id ? t('adminMobile.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {testModalOpen && (
        <div className="modal-overlay" onClick={() => setTestModalOpen(false)}>
          <div className="modal-content test-modal" onClick={e => e.stopPropagation()}>
            <h2>{t('adminMobile.sendTestNotification')}</h2>
            <p className="test-info">
              {t('adminMobile.testInfo')}
            </p>

            {stats && (stats.device_tokens.ios > 0 || stats.device_tokens.android > 0) && (
              <div className="test-platforms">
                {stats.device_tokens.ios > 0 && (
                  <span className="platform-badge ios">iOS ({stats.device_tokens.ios})</span>
                )}
                {stats.device_tokens.android > 0 && (
                  <span className="platform-badge android">Android ({stats.device_tokens.android})</span>
                )}
              </div>
            )}

            <div className="form-group">
              <label>{t('adminMobile.titleTemplate')}</label>
              <input
                type="text"
                value={testTitle}
                onChange={e => setTestTitle(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>{t('adminMobile.bodyTemplate')}</label>
              <textarea
                value={testBody}
                onChange={e => setTestBody(e.target.value)}
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setTestModalOpen(false)}>{t('common.cancel')}</button>
              <button
                className="save-btn"
                onClick={handleSendTest}
                disabled={testSending}
              >
                {testSending ? t('adminMobile.sending') : t('adminMobile.sendTest')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AdminMobile;
